// Einzige Schicht mit Three.js-Kenntnis. 1 Weltunit = 1 mm.
// Teil-Achsen lokal: X = Länge, Y = Stärke, Z = Breite. Position = Quader-Mittelpunkt.
import * as THREE from "three"
import { OrbitControls } from "three/addons/controls/OrbitControls.js"
import { GLTFExporter } from "three/addons/exporters/GLTFExporter.js"
import DimensionOverlay, { BOUNDS_LABELS } from "./dimensions"
import { cutoutsOf, isCircle, isThrough, cutoutAxis } from "../cutouts"
import { isHidden } from "../visibility"
import { cornersOf } from "../corners"
import { edgesOf } from "../edges"
import { groovesOf } from "../machinings"
import { mitersOf } from "../miters"
import { grainOf } from "../grain"
import { planeToWorld, worldToPlane, normalizePlane, tessellatedPoints } from "../sketch"
import { sectionPlaneParams } from "../section"
import { buildMachinedGeometry } from "./csg"
import { woodStripes, woodNoise } from "../photo"

const BACKGROUND = 0x1c1917
const GRID_SIZE = 4000      // mm
const GRID_CELL = 100       // mm
const PART_COLOR = 0xc99459
const SELECTED_EMISSIVE = 0x2f6feb
const GROUP_EMISSIVE = 0x4a2a08 // gedämpftes Esche: „gehört zum selben Korpus“
const REFERENCE_EMISSIVE = 0x0c6b4f // Lot-Grün: Referenzteil einer Beziehung
const COLLISION_EMISSIVE = 0x6b1414 // dezentes Rot: Teil durchdringt ein anderes
const ROOM_WARN_EMISSIVE = 0x6b4a0a // dezentes Amber: Teil verlässt den Raum
const EDGE_BAND_COLOR = 0xf59e0b
const EDGE_STRIP_MM = 3

// Foto-Modus: helles Warmgrau statt Werkstatt-Dunkel, eine Textur-Kachel
// deckt WOOD_TILE_MM Millimeter ab (map.repeat skaliert je Teil)
const PHOTO_BACKGROUND = 0xf5f5f4
const WOOD_TILE_MM = 512
const WOOD_CANVAS_PX = 256

// Mittelpunkt des Bohr-Einsatzes im lokalen Teil-Frame (x = Länge, y = Stärke,
// z = Breite). Der Zylinder ragt 0.2 mm aus der Eintrittsfläche heraus, damit
// keine Fläche bündig auf der Teilfläche liegt (Z-Fighting).
function borePlugOffset(bore, part) {
  const axis = cutoutAxis(bore)
  const entry = (extent) => bore.side === "bottom"
    ? -(extent / 2) + bore.depth_mm / 2 - 0.2
    : extent / 2 - bore.depth_mm / 2 + 0.2
  if (axis === "x") return new THREE.Vector3(entry(part.length_mm), bore.y_mm ?? 0, bore.z_mm ?? 0)
  if (axis === "z") return new THREE.Vector3(bore.x_mm ?? 0, bore.y_mm ?? 0, entry(part.width_mm))
  return new THREE.Vector3(bore.x_mm ?? 0, entry(part.thickness_mm), bore.z_mm ?? 0)
}

const DEG = Math.PI / 180

export default class ThreeRenderer {
  constructor(canvas, overlayElement) {
    this.canvas = canvas
    this.meshes = new Map()
    this.edgeStrips = new Map()
    this.edgeMaterial = null
    this.boreMeshes = new Map()
    this.boreMaterial = null
    this.sectionConfig = null
    this.sectionPlanes = [] // leer = kein Schnitt; sonst genau eine Ebene
    this.cylinderGeometry = new THREE.CylinderGeometry(0.5, 0.5, 1, 24)
    this.sphereGeometry = new THREE.SphereGeometry(1, 16, 12)
    this.coneGeometry = new THREE.ConeGeometry(1, 1, 14)
    this.selectionHandles = []
    this.handleDimsSignature = null
    this.handleBaseMaterial = new THREE.MeshBasicMaterial({ color: 0x0ea5e9, transparent: true, opacity: 0.85, depthTest: false })
    this.handleActiveMaterial = new THREE.MeshBasicMaterial({ color: 0xf59e0b, depthTest: false })
    // Farben nach Schreiner-/CAD-Konvention (Z-hoch wie SketchUp): Blau =
    // vertikale Achse (unser Y), Grün = horizontale Tiefenachse (unser Z)
    this.axisMaterials = {
      x: new THREE.MeshBasicMaterial({ color: 0xef4444, transparent: true, opacity: 0.9, depthTest: false }),
      y: new THREE.MeshBasicMaterial({ color: 0x3b82f6, transparent: true, opacity: 0.9, depthTest: false }),
      z: new THREE.MeshBasicMaterial({ color: 0x22c55e, transparent: true, opacity: 0.9, depthTest: false })
    }
    this.ringMaterials = {
      x: new THREE.MeshBasicMaterial({ color: 0xef4444, transparent: true, opacity: 0.45, depthTest: false }),
      y: new THREE.MeshBasicMaterial({ color: 0x3b82f6, transparent: true, opacity: 0.45, depthTest: false }),
      z: new THREE.MeshBasicMaterial({ color: 0x22c55e, transparent: true, opacity: 0.45, depthTest: false })
    }
    this.subSelected = null
    this.explodeFactor = 0   // Explosionsansicht (rein visuell, 0..1)
    this.visibleParts = null // Montage-Schritt: Set sichtbarer client_ids, null = alle
    this.relationLines = []
    this.relationMaterial = null
    this.selectedId = null
    this.referenceId = null
    this.onSelectedScreenPos = null
    this.raycaster = new THREE.Raycaster()
    this.boxGeometry = new THREE.BoxGeometry(1, 1, 1)
    this.overlay = overlayElement ? new DimensionOverlay(overlayElement) : null
    this.boundsOverlay = overlayElement ? new DimensionOverlay(overlayElement, BOUNDS_LABELS, "text-sky-300") : null
    this.boundsAnchor = new THREE.Object3D()
    this.boundsAnchor.userData.dims = null

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    // Schnittansicht klemmt die Ebene an einzelne Materialien statt global:
    // nur das Werkstück wird geschnitten, Griffe/Gizmos/Skizzen bleiben ganz.
    this.renderer.localClippingEnabled = true

    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(BACKGROUND)

    this.camera = new THREE.PerspectiveCamera(50, 1, 1, 50000)
    this.camera.position.set(1800, 1600, 2400)

    this.controls = new OrbitControls(this.camera, canvas)
    this.controls.target.set(0, 400, 0)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.1
    this.controls.maxPolarAngle = Math.PI / 2 - 0.02
    this.controls.minDistance = 200
    this.controls.maxDistance = 15000

    this.#buildEnvironment()

    this.resizeObserver = new ResizeObserver(() => this.#resize())
    this.resizeObserver.observe(canvas.parentElement)
    this.#resize()

    this.renderer.setAnimationLoop(() => this.#tick())
  }

  dispose() {
    this.renderer.setAnimationLoop(null)
    this.resizeObserver.disconnect()
    this.controls.dispose()
    this.overlay?.dispose()
    this.boundsOverlay?.dispose()
    this.scene.traverse((object) => {
      object.geometry?.dispose()
      object.material?.map?.dispose()
      object.material?.dispose()
    })
    this.woodTextures?.forEach((texture) => texture.dispose())
    this.renderer.dispose()
  }

  // --- Szene aus dem Dokument spiegeln -------------------------------------

  syncScene(doc) {
    const seen = new Set()
    for (const part of doc.allParts()) {
      seen.add(part.client_id)
      let mesh = this.meshes.get(part.client_id)
      if (!mesh) {
        mesh = new THREE.Mesh(
          this.boxGeometry,
          new THREE.MeshStandardMaterial({ color: PART_COLOR, roughness: 0.85 })
        )
        mesh.userData.clientId = part.client_id
        this.meshes.set(part.client_id, mesh)
        this.scene.add(mesh)
      }
      this.#applyGeometry(mesh, part)
      mesh.userData.hidden = isHidden(part) // ausgeblendet: unsichtbar + nicht pickbar
      mesh.position.set(part.pos_x, part.pos_y, part.pos_z)
      mesh.userData.basePosition ??= new THREE.Vector3()
      mesh.userData.basePosition.copy(mesh.position)
      mesh.rotation.set(part.rot_x * DEG, part.rot_y * DEG, part.rot_z * DEG)
      const materialColor = doc.getMaterial(part.material_id)?.color
      mesh.userData.grain = grainOf(part) // Holzoptik: Textur folgt der Maserrichtung
      this.#applyPartAppearance(mesh, materialColor || PART_COLOR)
      this.#syncEdgeStrips(part, mesh)
      this.#syncBores(part, mesh)
    }

    for (const [clientId, mesh] of this.meshes) {
      if (!seen.has(clientId)) {
        this.scene.remove(mesh)
        if (mesh.geometry !== this.boxGeometry) mesh.geometry.dispose()
        mesh.material.map?.dispose() // Foto-Modus: je Teil geklonte Holz-Textur
        mesh.material.dispose()
        this.meshes.delete(clientId)
        this.edgeStrips.get(clientId)?.forEach((strip) => this.scene.remove(strip))
        this.edgeStrips.delete(clientId)
        this.boreMeshes.get(clientId)?.forEach((bore) => this.scene.remove(bore))
        this.boreMeshes.delete(clientId)
      }
    }

    if (this.selectedId && !this.meshes.has(this.selectedId)) this.setSelected(null)
    // Neue Teil-/Umleimer-/Bohr-Materialien erben den aktiven Schnitt
    this.#refreshClipping()
    this.#updateBounds()
    if (this.sectionConfig) this.#updateSectionIndicator(this.sectionConfig)
    this.#applyAssemblyState()

    // Handles folgen Maß-/Rotationsänderungen und Bewegungen des selektierten Teils
    if (this.selectedId) {
      const mesh = this.meshes.get(this.selectedId)
      const signature = mesh ? this.#handleSignature(mesh) : null
      if (signature !== this.handleDimsSignature) this.#rebuildSelectionHandles()
      else this.#syncHandlePositions()
    }
  }

  // Gesamt-Bounding-Box aller Teile (für die Außenmaß-Anzeige ohne Selektion)
  #updateBounds() {
    if (this.meshes.size === 0) {
      this.boundsAnchor.userData.dims = null
      return
    }
    const box = new THREE.Box3()
    for (const mesh of this.meshes.values()) box.expandByObject(mesh)
    const size = box.getSize(new THREE.Vector3())
    box.getCenter(this.boundsAnchor.position)
    this.boundsAnchor.userData.dims = { x: Math.round(size.x), y: Math.round(size.y), z: Math.round(size.z) }
  }

  // Ohne Bearbeitungen: geteilte Box-Geometrie, Maße über scale.
  // Mit Durchbrüchen/Ecken: extrudiertes Shape mit Löchern (kein CSG nötig).
  // Sacklochbohrungen werden separat als dunkle Zylinder-Einsätze gezeigt.
  #applyGeometry(mesh, part) {
    const cutouts = cutoutsOf(part)
    const corners = cornersOf(part)
    const grooves = groovesOf(part)
    const miters = mitersOf(part)
    const signature = JSON.stringify([part.length_mm, part.thickness_mm, part.width_mm, cutouts, corners, grooves, miters])
    mesh.userData.dims = { x: part.length_mm, y: part.thickness_mm, z: part.width_mm }
    if (mesh.userData.signature === signature) return
    mesh.userData.signature = signature

    // Nut/Falz/Gehrung braucht echtes CSG — asynchron (WASM-Init beim ersten Mal);
    // bis dahin bleibt die bisherige Geometrie bzw. eine Platzhalter-Box stehen
    if (grooves.length > 0 || miters.length > 0) {
      if (mesh.geometry === this.boxGeometry) mesh.scale.set(part.length_mm, part.thickness_mm, part.width_mm)
      buildMachinedGeometry(part)
        .then((geometry) => {
          if (mesh.userData.signature !== signature) { geometry.dispose(); return }
          if (mesh.geometry !== this.boxGeometry) mesh.geometry.dispose()
          mesh.geometry = geometry
          mesh.scale.set(1, 1, 1)
        })
        .catch((error) => console.error("Bearbeitungs-Geometrie (Nut/Falz/Gehrung) fehlgeschlagen:", error))
      return
    }

    if (mesh.geometry !== this.boxGeometry) mesh.geometry.dispose()

    const throughCutouts = cutouts.filter(isThrough)
    mesh.userData.hasOutline = Boolean(part.meta?.outline)
    if (!mesh.userData.hasOutline && throughCutouts.length === 0 && Object.keys(corners).length === 0) {
      mesh.geometry = this.boxGeometry
      mesh.scale.set(part.length_mm, part.thickness_mm, part.width_mm)
    } else {
      mesh.geometry = this.#buildCutoutGeometry(part, throughCutouts)
      mesh.scale.set(1, 1, 1)
    }
  }

  // Außenkontur mit Eckbearbeitung. CCW in Shape-Koordinaten; Welt-z = -Shape-y,
  // daher liegt „vorn“ (z_min) bei +y. Fase = Sehne, Radius = Viertelbogen.
  #buildOutline(part) {
    // Skizzen-Formteile: freie Außenkontur aus meta.outline ([[x, z], …],
    // zentriert um den Mittelpunkt); Eckbearbeitungen gelten dann nicht
    if (part.meta?.outline) {
      const shape = new THREE.Shape()
      part.meta.outline.forEach(([ x, z ], index) => {
        if (index === 0) shape.moveTo(x, -z)
        else shape.lineTo(x, -z)
      })
      shape.closePath()
      return shape
    }

    const hx = part.length_mm / 2
    const hy = part.width_mm / 2
    const corners = cornersOf(part)
    const specs = [
      { key: "hl", x: -hx, y: -hy, dPrev: [0, -1], dNext: [1, 0] },
      { key: "hr", x: hx, y: -hy, dPrev: [1, 0], dNext: [0, 1] },
      { key: "vr", x: hx, y: hy, dPrev: [0, 1], dNext: [-1, 0] },
      { key: "vl", x: -hx, y: hy, dPrev: [-1, 0], dNext: [0, -1] }
    ]

    const shape = new THREE.Shape()
    specs.forEach((spec, index) => {
      const treatment = corners[spec.key]
      const size = treatment?.size_mm ?? 0
      const entry = [spec.x - spec.dPrev[0] * size, spec.y - spec.dPrev[1] * size]
      const exit = [spec.x + spec.dNext[0] * size, spec.y + spec.dNext[1] * size]

      if (index === 0) shape.moveTo(entry[0], entry[1])
      else shape.lineTo(entry[0], entry[1])
      if (!treatment) return

      if (treatment.type === "chamfer") {
        shape.lineTo(exit[0], exit[1])
      } else {
        const cx = entry[0] + spec.dNext[0] * size
        const cy = entry[1] + spec.dNext[1] * size
        const a0 = Math.atan2(entry[1] - cy, entry[0] - cx)
        const a1 = Math.atan2(exit[1] - cy, exit[0] - cx)
        shape.absarc(cx, cy, size, a0, a1, false)
      }
    })
    shape.closePath()
    return shape
  }

  #buildCutoutGeometry(part, cutouts) {
    const shape = this.#buildOutline(part)

    for (const cutout of cutouts) {
      const hole = new THREE.Path()
      // rotateX unten bildet Shape-y auf Welt--z ab → z-Versatz negieren
      const cx = cutout.x_mm
      const cy = -cutout.z_mm
      if (cutout.shape === "circle") {
        hole.absarc(cx, cy, cutout.diameter_mm / 2, 0, Math.PI * 2, true)
      } else {
        const hl = cutout.length_mm / 2
        const hw = cutout.width_mm / 2
        hole.moveTo(cx - hl, cy - hw)
        hole.lineTo(cx + hl, cy - hw)
        hole.lineTo(cx + hl, cy + hw)
        hole.lineTo(cx - hl, cy + hw)
        hole.closePath()
      }
      shape.holes.push(hole)
    }

    const geometry = new THREE.ExtrudeGeometry(shape, { depth: part.thickness_mm, bevelEnabled: false })
    geometry.rotateX(-Math.PI / 2)                    // Extrusionsachse → Y (Stärke)
    geometry.translate(0, -part.thickness_mm / 2, 0)  // Mittelpunkt zentrieren
    return geometry
  }

  // Emissive-Zustände überschreiben sich nicht mehr gegenseitig: jeder Setter
  // merkt sich nur seine Menge, die tatsächliche Farbe kommt zentral aus der
  // Rangfolge Selektion > Referenz > Warnung (Kollision/Raum) > Gruppe.
  #emissiveFor(clientId) {
    if (clientId === this.selectedId) return SELECTED_EMISSIVE
    if (this.multiHighlightIds?.has(clientId)) return SELECTED_EMISSIVE
    if (clientId === this.referenceId) return REFERENCE_EMISSIVE
    if (this.collisionIds?.has(clientId)) return COLLISION_EMISSIVE
    if (this.roomWarnIds?.has(clientId)) return ROOM_WARN_EMISSIVE
    if (this.groupHighlightIds?.has(clientId)) return GROUP_EMISSIVE
    return 0x000000
  }

  #applyEmissive(clientId) {
    this.meshes.get(clientId)?.material.emissive.setHex(this.#emissiveFor(clientId))
  }

  setSelected(clientId) {
    const previous = this.selectedId
    this.selectedId = clientId
    if (previous) this.#applyEmissive(previous)
    if (clientId) this.#applyEmissive(clientId)
    if (!clientId) this.overlay?.hide()
    this.#rebuildSelectionHandles()
  }

  // Referenzteil einer Beziehung (Ausrichten/Heften) grün hervorheben;
  // beim Löschen fällt es auf Warn-/Gruppen-Glimmen oder Neutral zurück
  setReferenceHighlight(clientId) {
    const previous = this.referenceId
    this.referenceId = clientId ?? null
    if (previous && previous !== this.referenceId) this.#applyEmissive(previous)
    if (this.referenceId) this.#applyEmissive(this.referenceId)
  }

  // Dezente Mithervorhebung einer Teilegruppe (alle Teile eines Korpus);
  // das selektierte Teil behält sein kräftiges Selektions-Blau.
  setGroupHighlight(clientIds = []) {
    const previous = this.groupHighlightIds ?? new Set()
    this.groupHighlightIds = new Set(clientIds)
    for (const id of new Set([ ...previous, ...this.groupHighlightIds ])) this.#applyEmissive(id)
  }

  // Additive Hervorhebung der Mehrfachauswahl: dieselbe Selektionsfarbe auf
  // allen Teilen im Set, ohne setSelected/Handles anzufassen (im Multi-Modus
  // gibt es keine Einzel-Selektion). Leeres Set räumt alles auf.
  setMultiHighlight(clientIds = []) {
    const previous = this.multiHighlightIds ?? new Set()
    this.multiHighlightIds = new Set(clientIds)
    for (const id of new Set([ ...previous, ...this.multiHighlightIds ])) this.#applyEmissive(id)
  }

  // Warn-Glimmen: kollidierende Teile rot, Teile außerhalb des Raums amber —
  // Selektion und Referenz gewinnen optisch (siehe #emissiveFor)
  setWarnings({ collisionIds = [], roomIds = [] } = {}) {
    const previous = new Set([ ...(this.collisionIds ?? []), ...(this.roomWarnIds ?? []) ])
    this.collisionIds = new Set(collisionIds)
    this.roomWarnIds = new Set(roomIds)
    for (const id of new Set([ ...previous, ...this.collisionIds, ...this.roomWarnIds ])) {
      this.#applyEmissive(id)
    }
  }

  // --- Raummaß (Nischen-/Raumbegrenzung, rein visuell) -----------------------

  // room = { enabled: true, width_mm, height_mm, depth_mm } | {} | null.
  // Boden, Rückwand und zwei Seitenwände als sehr dezente Flächen plus
  // Kantenlinien; zentriert um den Ursprung in X/Z, Boden bei y = 0 (wie das
  // Grid), Rückwand bei z = −depth/2. Kein Raycast-Treffer auf die Flächen.
  setRoom(room) {
    if (this.roomGroup) {
      this.scene.remove(this.roomGroup)
      this.roomGroup.traverse((o) => { o.geometry?.dispose(); o.material?.dispose() })
      this.roomGroup = null
    }
    if (room?.enabled !== true) return
    const { width_mm: w, height_mm: h, depth_mm: d } = room
    if (!(w > 0 && h > 0 && d > 0)) return

    this.roomGroup = new THREE.Group()
    this.roomGroup.visible = !this.photoMode
    const faceMaterial = new THREE.MeshBasicMaterial({
      color: 0x38bdf8, transparent: true, opacity: 0.05, side: THREE.DoubleSide, depthWrite: false
    })
    const addFace = (sizeU, sizeV, position, rotation) => {
      const face = new THREE.Mesh(new THREE.PlaneGeometry(sizeU, sizeV), faceMaterial)
      face.position.set(...position)
      face.rotation.set(...rotation)
      face.raycast = () => {} // Picking gehört den Teilen und dem Boden
      this.roomGroup.add(face)
    }
    addFace(w, d, [ 0, 0.4, 0 ], [ -Math.PI / 2, 0, 0 ])       // Boden (knapp überm Grid)
    addFace(w, h, [ 0, h / 2, -d / 2 ], [ 0, 0, 0 ])           // Rückwand
    addFace(d, h, [ -w / 2, h / 2, 0 ], [ 0, Math.PI / 2, 0 ]) // Seitenwand links
    addFace(d, h, [ w / 2, h / 2, 0 ], [ 0, -Math.PI / 2, 0 ]) // Seitenwand rechts

    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(w, h, d)),
      new THREE.LineBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.35 })
    )
    edges.position.set(0, h / 2, 0)
    edges.raycast = () => {}
    this.roomGroup.add(edges)
    this.scene.add(this.roomGroup)
  }

  // --- Foto-Modus (Präsentationsansicht mit Holz-Anmutung) -------------------

  // AN: Werkstatt-Visuals (Grid, Achsen, Handles, Beziehungs-Linien, Skizze)
  // aus, heller Hintergrund mit weichem Boden-Schatten, warme Lichter und je
  // Teil eine prozedurale Holz-Textur. AUS: alles exakt zurück — die
  // Original-Lichter waren nur unsichtbar, Farben kommen aus userData.baseColor.
  setPhotoMode(enabled) {
    if (Boolean(enabled) === Boolean(this.photoMode)) return
    this.photoMode = Boolean(enabled)

    if (this.photoMode) {
      this.grid.visible = false
      this.axes.visible = false
      this.selectionHandles.forEach((handle) => { handle.visible = false })
      if (this.sketchGroup) this.sketchGroup.visible = false
      if (this.roomGroup) this.roomGroup.visible = false

      this.savedBackground = this.scene.background
      this.scene.background = new THREE.Color(PHOTO_BACKGROUND)

      // vorherige Lichter merken und nur ausblenden — Zurückschalten ist
      // dann garantiert verlustfrei
      this.savedLights = [ this.hemisphere, this.sun ]
      this.savedLights.forEach((light) => { light.visible = false })
      this.photoLights = new THREE.Group()
      const softSky = new THREE.HemisphereLight(0xfff7ed, 0xd6d3d1, 1.15)
      const softKey = new THREE.DirectionalLight(0xffffff, 0.5)
      softKey.position.set(1600, 2600, 1800)
      this.photoLights.add(softSky, softKey)
      this.scene.add(this.photoLights)

      this.#buildShadowDisc()
      this.overlay?.container.classList.add("hidden") // Maß-Labels (beide Overlays + Mess-Label)
      for (const mesh of this.meshes.values()) this.#applyWoodMaterial(mesh)
    } else {
      this.grid.visible = true
      this.axes.visible = true
      this.selectionHandles.forEach((handle) => { handle.visible = true })
      if (this.sketchGroup) this.sketchGroup.visible = true
      if (this.roomGroup) this.roomGroup.visible = true

      this.scene.background = this.savedBackground ?? new THREE.Color(BACKGROUND)
      this.savedLights?.forEach((light) => { light.visible = true })
      if (this.photoLights) {
        this.scene.remove(this.photoLights)
        this.photoLights = null
      }
      if (this.shadowDisc) {
        this.scene.remove(this.shadowDisc)
        this.shadowDisc.geometry.dispose()
        this.shadowDisc.material.map?.dispose()
        this.shadowDisc.material.dispose()
        this.shadowDisc = null
      }
      this.overlay?.container.classList.remove("hidden")
      // Holzoptik im Arbeits-Modus eingeschaltet? Dann bleibt die Textur liegen.
      for (const mesh of this.meshes.values()) {
        if (this.woodLook) this.#applyWoodMaterial(mesh)
        else this.#removeWoodMaterial(mesh)
      }
    }
  }

  // Der WebGLRenderer läuft ohne preserveDrawingBuffer — nach dem Frame ist
  // der Buffer geleert. Deshalb synchron rendern und sofort auslesen.
  capturePhoto() {
    this.renderer.render(this.scene, this.camera)
    return this.canvas.toDataURL("image/png")
  }

  // --- 3D-Export als glTF (.glb) ---------------------------------------------

  // Exportiert eine temporäre Group mit Klonen der sichtbaren Teil-Meshes
  // (inkl. Materialfarben, ohne Handles/Grid/Gizmos/Skizzen) binär als Blob.
  // Geometrien werden geteilt (der Exporter liest nur), die frischen
  // Materialien danach wieder entsorgt. basePosition statt position, damit
  // eine aktive Explosionsansicht nicht mit exportiert wird.
  async exportGlb() {
    const group = new THREE.Group()
    for (const mesh of this.meshes.values()) {
      if (!mesh.visible) continue
      const clone = new THREE.Mesh(mesh.geometry, new THREE.MeshStandardMaterial({
        color: mesh.userData.baseColor ?? PART_COLOR, roughness: 0.85
      }))
      clone.position.copy(mesh.userData.basePosition ?? mesh.position)
      clone.quaternion.copy(mesh.quaternion)
      clone.scale.copy(mesh.scale)
      group.add(clone)
    }
    if (group.children.length === 0) throw new Error("Keine sichtbaren Teile")

    try {
      const result = await new GLTFExporter().parseAsync(group, { binary: true })
      return new Blob([ result ], { type: "model/gltf-binary" })
    } finally {
      group.children.forEach((clone) => clone.material.dispose())
    }
  }

  // --- Holzoptik im Arbeits-Modus --------------------------------------------

  // Dieselben prozeduralen Texturen wie im Foto-Modus, aber ohne dessen
  // Hintergrund-/Licht-/Sichtbarkeits-Umbau: Grid, Handles und Maß-Labels
  // bleiben. Der Foto-Modus gewinnt immer — ist er aktiv, tragen die Meshes
  // ohnehin schon Holz, und beim Verlassen entscheidet dieser Schalter, ob es
  // bleibt (siehe setPhotoMode).
  setWoodLook(enabled) {
    if (Boolean(enabled) === Boolean(this.woodLook)) return
    this.woodLook = Boolean(enabled)
    if (this.photoMode) return
    for (const mesh of this.meshes.values()) {
      if (this.woodLook) this.#applyWoodMaterial(mesh)
      else this.#removeWoodMaterial(mesh)
    }
  }

  // Holz-Darstellung aktiv? Foto-Modus oder der Holzoptik-Schalter.
  #woodActive() {
    return Boolean(this.photoMode || this.woodLook)
  }

  // Läuft in jedem syncScene: normal wird die Materialfarbe gesetzt; bei
  // aktiver Holz-Darstellung stattdessen die zur Farbe passende Holz-Textur
  // (erneut) angewendet — die Farbzuweisung würde die Textur multiplikativ
  // tönen und den Holz-Look zerstören.
  #applyPartAppearance(mesh, color) {
    mesh.userData.baseColor = color
    if (this.#woodActive()) this.#applyWoodMaterial(mesh)
    else mesh.material.color.set(color)
  }

  #applyWoodMaterial(mesh) {
    const hex = "#" + new THREE.Color(mesh.userData.baseColor ?? PART_COLOR).getHexString()
    if (mesh.userData.woodHex !== hex || !mesh.material.map) {
      mesh.userData.woodHex = hex
      // je Teil ein Klon (eigenes repeat), Bilddaten kommen aus dem Farb-Cache
      const texture = this.#woodTextureFor(hex).clone()
      texture.needsUpdate = true
      mesh.material.map?.dispose()
      mesh.material.map = texture
      mesh.material.needsUpdate = true
    }
    // Grundton steckt in der Textur — Weiß verhindert doppelte Tönung
    mesh.material.color.set(0xffffff)
    mesh.material.roughness = 0.7
    // Maserrichtung: die Streifen laufen in der Textur längs (u). Bei Maser
    // „quer“ wird die Textur um 90° um ihre Mitte gedreht, damit die Fasern
    // der Teil-Breite folgen — genau das, was der Schreiner sehen will.
    const crossGrain = mesh.userData.grain === "width"
    mesh.material.map.center.set(0.5, 0.5)
    mesh.material.map.rotation = crossGrain ? Math.PI / 2 : 0
    // Maserung an Länge/Breite koppeln: Box-Geometrie hat UV 0..1 über die
    // ganze Fläche, Extrude-Geometrie (Ausschnitte/Formteile) UVs in mm.
    // Gedreht aufgelegt tauschen Länge und Breite ihre Rolle.
    const dims = mesh.userData.dims
    if (mesh.geometry === this.boxGeometry && dims) {
      const u = (crossGrain ? dims.z : dims.x) / WOOD_TILE_MM
      const v = (crossGrain ? dims.x : dims.z) / WOOD_TILE_MM
      mesh.material.map.repeat.set(u, v)
    } else {
      mesh.material.map.repeat.set(1 / WOOD_TILE_MM, 1 / WOOD_TILE_MM)
    }
  }

  #removeWoodMaterial(mesh) {
    mesh.material.map?.dispose()
    mesh.material.map = null
    mesh.material.needsUpdate = true
    mesh.material.roughness = 0.85
    mesh.material.color.set(mesh.userData.baseColor ?? PART_COLOR)
    delete mesh.userData.woodHex
  }

  // Holz-Textur je Farbe cachen: Grundton = Materialfarbe, darüber gewellte
  // halbtransparente Längsstreifen (Maserung) + feines Rauschen. Die
  // Streifen-Parametrik kommt deterministisch aus photo.js (testbar).
  #woodTextureFor(hex) {
    this.woodTextures ??= new Map()
    let texture = this.woodTextures.get(hex)
    if (texture) return texture

    const size = WOOD_CANVAS_PX
    const canvas = document.createElement("canvas")
    canvas.width = canvas.height = size
    const ctx = canvas.getContext("2d")
    ctx.fillStyle = hex
    ctx.fillRect(0, 0, size, size)

    const dark = new THREE.Color(hex).multiplyScalar(0.5)
    const light = new THREE.Color(hex).multiplyScalar(1.35)
    const style = (c) => `rgb(${Math.min(255, Math.round(c.r * 255))}, ` +
      `${Math.min(255, Math.round(c.g * 255))}, ${Math.min(255, Math.round(c.b * 255))})`

    // Maserung: Streifen verlaufen längs (x = Faser-/Längsrichtung)
    ctx.fillStyle = style(dark)
    for (const stripe of woodStripes(hex)) {
      ctx.globalAlpha = stripe.alpha
      const height = Math.max(1, stripe.width * size)
      for (let x = 0; x < size; x += 2) {
        const mid = (stripe.y + stripe.waveAmp *
          Math.sin(stripe.wavePhase + stripe.waveFreq * Math.PI * 2 * (x / size))) * size
        ctx.fillRect(x, mid - height / 2, 2, height)
      }
    }

    // feines Rauschen (Poren)
    for (const dot of woodNoise(hex)) {
      ctx.globalAlpha = dot.alpha
      ctx.fillStyle = dot.dark ? style(dark) : style(light)
      ctx.fillRect(Math.floor(dot.x * size), Math.floor(dot.y * size), 1, 1)
    }
    ctx.globalAlpha = 1

    texture = new THREE.CanvasTexture(canvas)
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping
    texture.colorSpace = THREE.SRGBColorSpace
    this.woodTextures.set(hex, texture)
    return texture
  }

  // Weicher Boden-„Schatten“: radialer Verlauf auf einer liegenden Scheibe
  // unter der Gesamt-Bounding-Box — kein echtes Shadow-Mapping nötig
  #buildShadowDisc() {
    const gradientSize = 128
    const canvas = document.createElement("canvas")
    canvas.width = canvas.height = gradientSize
    const ctx = canvas.getContext("2d")
    const gradient = ctx.createRadialGradient(
      gradientSize / 2, gradientSize / 2, 0,
      gradientSize / 2, gradientSize / 2, gradientSize / 2
    )
    gradient.addColorStop(0, "rgba(28, 25, 23, 0.30)")
    gradient.addColorStop(0.65, "rgba(28, 25, 23, 0.12)")
    gradient.addColorStop(1, "rgba(28, 25, 23, 0)")
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, gradientSize, gradientSize)

    const texture = new THREE.CanvasTexture(canvas)
    const dims = this.boundsAnchor.userData.dims
    const radius = dims ? Math.max(dims.x, dims.z) * 0.8 + 250 : 1200
    this.shadowDisc = new THREE.Mesh(
      new THREE.CircleGeometry(radius, 48),
      new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false })
    )
    this.shadowDisc.rotation.x = -Math.PI / 2
    this.shadowDisc.position.set(this.boundsAnchor.position.x, 0.5, this.boundsAnchor.position.z)
    this.shadowDisc.raycast = () => {}
    this.scene.add(this.shadowDisc)
  }

  // --- Explosionsansicht & Montage-Schritte (rein visuell, keine doc-Änderung)

  // factor 0..1: jedes Teil wandert um (Teilzentrum − Szenenzentrum) · factor · 0.6
  // vom Zentrum weg; factor 0 stellt alle Positionen exakt wieder her.
  setExplode(factor) {
    this.explodeFactor = factor
    this.#applyExplode()
  }

  // clientIds = sichtbare Teile (Montageschritt 1..n), null = alle sichtbar.
  // Begleit-Meshes (Umleimer, Bohrungen) werden mit versteckt.
  setVisibleParts(clientIds) {
    this.visibleParts = clientIds ? new Set(clientIds) : null
    this.#applyVisibility()
  }

  // Nach jedem syncScene erneut anwenden: die Teil-Positionen wurden gerade
  // frisch aus dem Dokument gesetzt, der Explosions-Offset und die
  // Sichtbarkeit (Montage-Schritt + ausgeblendete Teile) müssen das überleben.
  #applyAssemblyState() {
    if (this.explodeFactor > 0) this.#applyExplode()
    this.#applyVisibility()
  }

  #applyExplode() {
    if (this.meshes.size === 0) return
    const center = this.boundsAnchor.position // Mitte der Gesamt-Bounding-Box
    const offset = new THREE.Vector3()
    for (const [ clientId, mesh ] of this.meshes) {
      const base = mesh.userData.basePosition
      if (!base) continue
      offset.copy(base).sub(center).multiplyScalar(this.explodeFactor * 0.6)
      mesh.position.copy(base).add(offset)
      const strips = this.edgeStrips.get(clientId)
      if (strips) this.#positionAttachedMeshes(strips, mesh)
      const bores = this.boreMeshes.get(clientId)
      if (bores) this.#positionAttachedMeshes(bores, mesh)
    }
  }

  #applyVisibility() {
    for (const [ clientId, mesh ] of this.meshes) {
      const visible = !mesh.userData.hidden && (!this.visibleParts || this.visibleParts.has(clientId))
      mesh.visible = visible
      this.edgeStrips.get(clientId)?.forEach((strip) => { strip.visible = visible })
      this.boreMeshes.get(clientId)?.forEach((bore) => { bore.visible = visible })
    }
  }

  // --- Ecken-/Kanten-Handles am selektierten Teil ----------------------------

  #rebuildSelectionHandles() {
    this.selectionHandles.forEach((handle) => {
      this.scene.remove(handle)
      if (handle.userData.ownGeometry) handle.geometry.dispose()
    })
    this.selectionHandles = []
    this.handleDimsSignature = null

    const mesh = this.selectedId && this.meshes.get(this.selectedId)
    if (!mesh?.userData.dims) return
    const { x: l, z: b } = mesh.userData.dims
    this.handleDimsSignature = this.#handleSignature(mesh)

    // Formteile (freie Kontur): keine Ecken-/Kanten-Griffe — Rechteck-Semantik
    // greift dort nicht; Verschieben/Drehen bleibt über Pfeile und Ringe
    if (!mesh.userData.hasOutline) {
      const corners = { vl: [ -l / 2, 0, -b / 2 ], vr: [ l / 2, 0, -b / 2 ], hl: [ -l / 2, 0, b / 2 ], hr: [ l / 2, 0, b / 2 ] }
      for (const [key, offset] of Object.entries(corners)) {
        this.#addHandle("corner", key, offset, this.sphereGeometry, [ 16, 16, 16 ])
      }
      const edges = {
        l1: { offset: [ 0, 0, -b / 2 - 3 ], size: [ 70, 12, 12 ] },
        l2: { offset: [ 0, 0, b / 2 + 3 ], size: [ 70, 12, 12 ] },
        b1: { offset: [ -l / 2 - 3, 0, 0 ], size: [ 12, 12, 70 ] },
        b2: { offset: [ l / 2 + 3, 0, 0 ], size: [ 12, 12, 70 ] }
      }
      for (const [key, spec] of Object.entries(edges)) {
        this.#addHandle("edge", key, spec.offset, this.boxGeometry, spec.size)
      }
    }

    // Drei Dreh-Ringe in Achsfarben: Ziehen entlang eines Rings dreht das Teil
    // in genau dieser Ring-Ebene, es folgt dem Finger. Gestaffelte Radien,
    // damit sich die Ringe an den Kreuzungen weniger nahekommen (Fehlgriffe)
    const extents = this.#worldExtents(mesh)
    const ringRadii = {
      x: Math.max(extents.y, extents.z, 60) + 50,
      y: Math.max(extents.x, extents.z, 60) + 85,
      z: Math.max(extents.x, extents.y, 60) + 120
    }
    const ringSpecs = [
      { axis: "x", orient: (g) => g.rotateY(Math.PI / 2) },
      { axis: "y", orient: (g) => g.rotateX(Math.PI / 2) },
      { axis: "z", orient: null }
    ]
    for (const spec of ringSpecs) {
      const geometry = new THREE.TorusGeometry(ringRadii[spec.axis], 9, 10, 48)
      spec.orient?.(geometry)
      const ring = this.#addHandle("rotate", `rot-${spec.axis}`, null, geometry, [ 1, 1, 1 ], this.ringMaterials[spec.axis])
      ring.userData.worldOffset = new THREE.Vector3(0, 0, 0)
      ring.userData.ownGeometry = true
      ring.userData.ringRadius = ringRadii[spec.axis]
    }

    // Verschiebe-Pfeile außerhalb des größten Rings, dessen Ebene die Achse
    // enthält — Pfeile und Ringe können sich so nie überlappen
    const arrowOffsets = {
      x: Math.max(ringRadii.y, ringRadii.z) + 60,
      y: Math.max(ringRadii.x, ringRadii.z) + 60,
      z: Math.max(ringRadii.x, ringRadii.y) + 60
    }
    const axisSpecs = [
      { key: "x", offset: [ arrowOffsets.x, 0, 0 ], euler: [ 0, 0, -Math.PI / 2 ] },
      { key: "y", offset: [ 0, arrowOffsets.y, 0 ], euler: null },
      { key: "z", offset: [ 0, 0, arrowOffsets.z ], euler: [ Math.PI / 2, 0, 0 ] }
    ]
    for (const spec of axisSpecs) {
      const arrow = this.#addHandle("axis", spec.key, null, this.coneGeometry, [ 38, 66, 38 ], this.axisMaterials[spec.key])
      arrow.userData.worldOffset = new THREE.Vector3(...spec.offset)
      if (spec.euler) {
        arrow.userData.fixedQuaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(...spec.euler))
      }
    }

    this.#syncHandlePositions()
    this.#refreshHandleMaterials()
  }

  #handleSignature(mesh) {
    return JSON.stringify([ mesh.userData.dims, mesh.rotation.x, mesh.rotation.y, mesh.rotation.z ])
  }

  // Welt-Halbausdehnung des (rotierten) Teils je Achse
  #worldExtents(mesh) {
    const dims = mesh.userData.dims
    const axes = [ new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 1) ]
      .map((v) => v.applyQuaternion(mesh.quaternion))
    const extent = (i) =>
      Math.abs(axes[0].getComponent(i)) * dims.x / 2 +
      Math.abs(axes[1].getComponent(i)) * dims.y / 2 +
      Math.abs(axes[2].getComponent(i)) * dims.z / 2
    return { x: extent(0), y: extent(1), z: extent(2) }
  }

  #addHandle(type, key, offset, geometry, size, material = this.handleBaseMaterial) {
    const handle = new THREE.Mesh(geometry, material)
    handle.scale.set(...size)
    handle.renderOrder = 10 // depthTest aus → immer sichtbar über dem Teil
    handle.userData = { handleType: type, handleKey: key, baseMaterial: material }
    if (offset) handle.userData.localOffset = new THREE.Vector3(...offset)
    this.scene.add(handle)
    this.selectionHandles.push(handle)
    return handle
  }

  #syncHandlePositions() {
    const mesh = this.selectedId && this.meshes.get(this.selectedId)
    if (mesh) this.#positionAttachedMeshes(this.selectionHandles, mesh)
  }

  pickHandle(event) {
    if (this.selectionHandles.length === 0) return null
    this.raycaster.setFromCamera(this.#pointerNdc(event), this.camera)
    const hit = this.raycaster.intersectObjects(this.selectionHandles)[0]
    if (!hit) return null
    return { type: hit.object.userData.handleType, key: hit.object.userData.handleKey }
  }

  setSubSelected(sub) {
    this.subSelected = sub
    this.#refreshHandleMaterials()
  }

  // Während eines Griff-Drags leuchtet der tatsächlich gegriffene Griff auf —
  // sofort sichtbar, welcher Ring/Pfeil erwischt wurde (Kreuzungs-Fehlgriffe)
  setDragHandle(handle) {
    this.dragHandle = handle
    this.#refreshHandleMaterials()
  }

  #refreshHandleMaterials() {
    const matches = (handle, ref) =>
      ref && handle.userData.handleType === ref.type && handle.userData.handleKey === ref.key
    for (const handle of this.selectionHandles) {
      const active = matches(handle, this.dragHandle) || matches(handle, this.subSelected)
      handle.material = active ? this.handleActiveMaterial : handle.userData.baseMaterial
    }
  }

  // Transiente Bewegung während des Drags — kein Document-Update pro Frame
  setPreviewPosition(clientId, { pos_x, pos_y, pos_z }) {
    const mesh = this.meshes.get(clientId)
    if (!mesh) return
    mesh.position.set(pos_x, pos_y, pos_z)
    const strips = this.edgeStrips.get(clientId)
    if (strips) this.#positionAttachedMeshes(strips, mesh)
    const bores = this.boreMeshes.get(clientId)
    if (bores) this.#positionAttachedMeshes(bores, mesh)
    if (clientId === this.selectedId) this.#syncHandlePositions()
  }

  // Sacklochbohrungen: dunkler Zylinder-Einsatz bündig zur Bohrseite —
  // wirkt von außen wie eine Bohrung, ohne CSG zu brauchen.
  // Stirnbohrungen (axis x/z) sitzen in der Schmalseite, ihr Zylinder wird
  // zusätzlich auf die lokale Bohrachse gekippt (localQuaternion).
  #syncBores(part, mesh) {
    const bores = cutoutsOf(part).filter((c) => isCircle(c) && c.depth_mm)
    const signature = JSON.stringify([bores, part.length_mm, part.thickness_mm, part.width_mm])
    let meshes = this.boreMeshes.get(part.client_id) ?? []

    if (mesh.userData.boreSignature !== signature) {
      mesh.userData.boreSignature = signature
      meshes.forEach((bore) => this.scene.remove(bore))
      this.boreMaterial ??= new THREE.MeshStandardMaterial({ color: 0x151210, roughness: 1 })
      meshes = bores.map((bore) => {
        const plug = new THREE.Mesh(this.cylinderGeometry, this.boreMaterial)
        const height = bore.depth_mm + 0.4
        plug.scale.set(bore.diameter_mm, height, bore.diameter_mm)
        plug.userData.localOffset = borePlugOffset(bore, part)
        // Stirnbohrung: Zylinder (Achse y) auf die lokale Bohrachse kippen
        const axis = cutoutAxis(bore)
        if (axis !== "y") {
          plug.userData.localQuaternion = new THREE.Quaternion().setFromEuler(
            axis === "x" ? new THREE.Euler(0, 0, Math.PI / 2) : new THREE.Euler(Math.PI / 2, 0, 0))
        }
        plug.raycast = () => {}
        this.scene.add(plug)
        return plug
      })
      this.boreMeshes.set(part.client_id, meshes)
    }

    this.#positionAttachedMeshes(meshes, mesh)
  }

  // Transiente Maß-Vorschau beim Kanten-Drag (Skalierungs-Trick; Bearbeitungen
  // verzerren kurz — nach dem Loslassen wird die Geometrie sauber neu gebaut)
  setPreviewResize(clientId, dims, pos) {
    const mesh = this.meshes.get(clientId)
    if (!mesh) return
    const base = mesh.userData.dims
    if (mesh.geometry === this.boxGeometry) mesh.scale.set(dims.x, dims.y, dims.z)
    else mesh.scale.set(dims.x / base.x, dims.y / base.y, dims.z / base.z)
    mesh.position.set(pos.pos_x, pos.pos_y, pos.pos_z)
  }

  // Umleimer-Streifen: eigene Meshes in Weltkoordinaten (Kind-Meshes würden
  // die Parent-Skalierung der Box-Teile erben und verzerren)
  #syncEdgeStrips(part, mesh) {
    const edges = edgesOf(part)
    const signature = JSON.stringify([edges, part.length_mm, part.thickness_mm, part.width_mm])
    let strips = this.edgeStrips.get(part.client_id) ?? []

    if (mesh.userData.edgeSignature !== signature) {
      mesh.userData.edgeSignature = signature
      strips.forEach((strip) => this.scene.remove(strip))
      this.edgeMaterial ??= new THREE.MeshStandardMaterial({
        color: EDGE_BAND_COLOR, emissive: EDGE_BAND_COLOR, emissiveIntensity: 0.25, roughness: 0.6
      })
      strips = edges.map((key) => {
        const strip = new THREE.Mesh(this.boxGeometry, this.edgeMaterial)
        const spec = this.#edgeStripSpec(part, key)
        strip.scale.set(...spec.size)
        strip.userData.localOffset = new THREE.Vector3(...spec.offset)
        strip.raycast = () => {} // Picking gehört dem Teil, nicht dem Streifen
        this.scene.add(strip)
        return strip
      })
      this.edgeStrips.set(part.client_id, strips)
    }

    this.#positionAttachedMeshes(strips, mesh)
  }

  #edgeStripSpec(part, key) {
    const { length_mm: l, thickness_mm: s, width_mm: b } = part
    const t = EDGE_STRIP_MM
    switch (key) {
      case "l1": return { offset: [0, 0, -(b + t) / 2], size: [l, s + 1, t] }
      case "l2": return { offset: [0, 0, (b + t) / 2], size: [l, s + 1, t] }
      case "b1": return { offset: [-(l + t) / 2, 0, 0], size: [t, s + 1, b] }
      default: return { offset: [(l + t) / 2, 0, 0], size: [t, s + 1, b] }
    }
  }

  // Positioniert Begleit-Meshes relativ zum Teil: localOffset dreht mit,
  // worldOffset bleibt weltfest (Hebe-Pfeil, Dreh-Ring)
  #positionAttachedMeshes(attached, mesh) {
    for (const item of attached) {
      if (item.userData.worldOffset) {
        if (item.userData.fixedQuaternion) item.quaternion.copy(item.userData.fixedQuaternion)
        else item.quaternion.identity()
        item.position.copy(item.userData.worldOffset).add(mesh.position)
      } else {
        item.quaternion.copy(mesh.quaternion)
        // localQuaternion kippt das Begleit-Mesh zusätzlich im Teil-Frame
        // (Stirnbohrungen: Zylinder-Achse entlang der lokalen x-/z-Achse)
        if (item.userData.localQuaternion) item.quaternion.multiply(item.userData.localQuaternion)
        item.position.copy(item.userData.localOffset).applyQuaternion(mesh.quaternion).add(mesh.position)
      }
    }
  }

  // --- Schnittansicht (Clipping-Ebene durchs Modell) -------------------------

  // min/max der Gesamt-Bounding-Box auf einer Weltachse — der Bereich für
  // den Schnitt-Slider. null ohne Teile.
  sectionRange(axis) {
    const dims = this.boundsAnchor.userData.dims
    if (!dims) return null
    const center = this.boundsAnchor.position[axis]
    const half = dims[axis] / 2
    return { min: Math.floor(center - half), max: Math.ceil(center + half) }
  }

  // config = { axis: "x"|"y"|"z", offsetMm, flip } | null. LOKALES Clipping:
  // die Ebene hängt an den Materialien der Werkstück-Geometrie (Teil-Meshes,
  // Umleimer-Streifen, Bohr-Einsätze) — Auswahl-Griffe, Gizmos, Skizzen-Linien,
  // Mess-Marker und der Ebenen-Indikator bleiben deshalb vollständig sichtbar.
  // flip schneidet von der anderen Seite. null räumt überall auf.
  setSectionPlane(config) {
    this.sectionConfig = config ?? null
    const params = sectionPlaneParams(this.sectionConfig)
    if (!params) {
      this.sectionPlanes = []
      this.#refreshClipping()
      if (this.sectionIndicator) this.sectionIndicator.visible = false
      return
    }
    // Ebenen-Objekt wiederverwenden: beim Schieben ändert sich nur die
    // Konstante, die Anzahl der Planes bleibt 1 (kein Shader-Neubau).
    const plane = this.sectionPlanes[0] ?? new THREE.Plane()
    plane.set(new THREE.Vector3(...params.normal), params.constant)
    this.sectionPlanes = [ plane ]
    this.#refreshClipping()
    this.#updateSectionIndicator(this.sectionConfig)
  }

  // Alle Materialien, die der Schnitt betrifft. Teil-Meshes haben je ein
  // eigenes MeshStandardMaterial; edgeMaterial/boreMaterial sind jeweils EINE
  // geteilte Instanz für alle Streifen bzw. Einsätze — unkritisch, weil alle
  // ihre Nutzer zum Werkstück gehören und geschnitten werden sollen.
  #clippedMaterials() {
    const materials = [ ...this.meshes.values() ].map((mesh) => mesh.material)
    if (this.edgeMaterial) materials.push(this.edgeMaterial)
    if (this.boreMaterial) materials.push(this.boreMaterial)
    return materials
  }

  #refreshClipping() {
    for (const material of this.#clippedMaterials()) this.#applyClippingTo(material)
  }

  // needsUpdate nur beim Wechsel von/zu „keine Ebene“: die Zahl der Planes
  // steckt im Shader-Programm, das Three.js dann neu übersetzen muss.
  #applyClippingTo(material) {
    if (!material) return
    const had = Boolean(material.clippingPlanes?.length)
    const has = this.sectionPlanes.length > 0
    material.clippingPlanes = has ? this.sectionPlanes : null
    if (had !== has) material.needsUpdate = true
  }

  // Dezentes Rechteck exakt an der Schnittposition — seit dem lokalen
  // Clipping wird der Indikator selbst nicht mehr geschnitten, der frühere
  // 0,8-mm-Versatz gegen das Wegflackern entfällt. depthWrite:false und der
  // Standard-Tiefentest (≤) halten ihn auch bei bündigen Teilflächen ruhig.
  #updateSectionIndicator(config) {
    if (!this.sectionIndicator) {
      this.sectionIndicator = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({
          color: 0x38bdf8, transparent: true, opacity: 0.06, side: THREE.DoubleSide, depthWrite: false
        })
      )
      this.sectionIndicator.renderOrder = 1
      this.sectionIndicator.raycast = () => {}
      this.scene.add(this.sectionIndicator)
    }
    const dims = this.boundsAnchor.userData.dims
    const indicator = this.sectionIndicator
    indicator.visible = Boolean(dims)
    if (!dims) return

    const margin = 200
    const center = this.boundsAnchor.position
    indicator.rotation.set(0, 0, 0)
    indicator.position.copy(center)
    if (config.axis === "x") {
      indicator.rotation.y = Math.PI / 2 // Ebenen-Normale → Welt-x
      indicator.scale.set(dims.z + margin, dims.y + margin, 1)
    } else if (config.axis === "y") {
      indicator.rotation.x = -Math.PI / 2 // Ebenen-Normale → Welt-y
      indicator.scale.set(dims.x + margin, dims.z + margin, 1)
    } else {
      indicator.scale.set(dims.x + margin, dims.y + margin, 1)
    }
    indicator.position[config.axis] = config.offsetMm
  }

  setOrbitEnabled(enabled) {
    this.controls.enabled = enabled
  }

  // Kameraabstand zum Orbit-Ziel skalieren (Pinch-Zoom im Skizzenmodus,
  // wo die Orbit-Steuerung während eines Zeichen-Strichs aus ist)
  zoomBy(factor) {
    const offset = this.camera.position.clone().sub(this.controls.target)
    const distance = Math.min(Math.max(offset.length() * factor, this.controls.minDistance), this.controls.maxDistance)
    this.camera.position.copy(this.controls.target).add(offset.setLength(distance))
  }

  // Bündig eingerastet: Selektions-Highlight wird grün
  setSnapActive(active) {
    if (!this.selectedId) return
    const mesh = this.meshes.get(this.selectedId)
    mesh?.material.emissive.setHex(active ? 0x15803d : SELECTED_EMISSIVE)
  }

  // --- Kamera-Fokus (Doppeltipp) ---------------------------------------------

  // Standard-Ansichten (perspektivisch): ISO, Draufsicht (+y), Vorn (-z),
  // Hinten (+z), Links (-x), Rechts (+x); „side" bleibt als Alias für
  // Rechts erhalten. Keine Untersicht: die Orbit-Steuerung klemmt die
  // Kamera bewusst über dem Werkstattboden (maxPolarAngle).
  setStandardView(name) {
    const dims = this.boundsAnchor.userData.dims
    const center = dims ? this.boundsAnchor.position.clone() : new THREE.Vector3(0, 300, 0)
    const size = dims ? Math.max(dims.x, dims.y, dims.z, 500) : 1500
    const directions = {
      iso: new THREE.Vector3(1, 0.75, 1),
      top: new THREE.Vector3(0.02, 1, 0.02),
      front: new THREE.Vector3(0, 0.12, -1),
      back: new THREE.Vector3(0, 0.12, 1),
      left: new THREE.Vector3(-1, 0.12, 0),
      right: new THREE.Vector3(1, 0.12, 0),
      side: new THREE.Vector3(1, 0.12, 0)
    }
    const direction = (directions[name] ?? directions.iso).clone().normalize()
    const distance = Math.min(Math.max(size * 1.7, this.controls.minDistance), this.controls.maxDistance)
    this.focusAnim = {
      fromTarget: this.controls.target.clone(),
      toTarget: center,
      fromPosition: this.camera.position.clone(),
      toPosition: center.clone().add(direction.multiplyScalar(distance)),
      start: performance.now()
    }
  }

  setRotateEnabled(enabled) {
    this.controls.enableRotate = enabled
  }

  // Wie viele Welt-mm entsprechen `px` Bildschirm-Pixeln am Orbit-Ziel —
  // für zoomabhängige Tipp-Toleranzen (z. B. Kanten in der Skizze treffen)
  worldToleranceFor(px = 16) {
    const distance = this.camera.position.distanceTo(this.controls.target)
    const worldPerPixel = (2 * distance * Math.tan((this.camera.fov * Math.PI) / 360)) / this.canvas.clientHeight
    return Math.max(8, px * worldPerPixel)
  }

  // --- Skizzen-Visuals (Linienzüge auf der Bodenebene) -----------------------

  setSketchVisuals(data) {
    if (this.sketchGroup) {
      this.scene.remove(this.sketchGroup)
      this.sketchGroup.traverse((o) => o.geometry?.dispose())
      this.sketchGroup = null
    }
    if (!data) return
    this.sketchMaterial ??= new THREE.LineBasicMaterial({ color: 0x38bdf8, depthTest: false })
    this.sketchDraftMaterial ??= new THREE.LineBasicMaterial({ color: 0xf59e0b, depthTest: false })
    this.sketchIdleMaterial ??= new THREE.LineBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.4, depthTest: false })
    this.sketchEdgeMaterial ??= new THREE.LineBasicMaterial({ color: 0xf59e0b, depthTest: false })
    this.sketchFaceMaterial ??= new THREE.MeshBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.08, depthTest: false, side: THREE.DoubleSide })
    this.sketchFaceSelectedMaterial ??= new THREE.MeshBasicMaterial({ color: 0xd97706, transparent: true, opacity: 0.35, depthTest: false, side: THREE.DoubleSide })
    this.sketchGuideMaterial ??= new THREE.LineDashedMaterial({ color: 0x94a3b8, dashSize: 24, gapSize: 16, depthTest: false })
    this.sketchGuideIdleMaterial ??= new THREE.LineDashedMaterial({ color: 0x94a3b8, transparent: true, opacity: 0.35, dashSize: 24, gapSize: 16, depthTest: false })
    this.sketchSnapMaterial ??= new THREE.MeshBasicMaterial({ color: 0x34d399, depthTest: false })
    this.sketchSnapLineMaterial ??= new THREE.LineDashedMaterial({ color: 0x34d399, dashSize: 12, gapSize: 10, depthTest: false })
    this.sketchGroup = new THREE.Group()
    this.sketchGroup.renderOrder = 9
    this.sketchGroup.visible = !this.photoMode // Foto-Modus: Skizze bleibt aus

    // Zeichen-Helfer sind ebenenspezifisch — jede Skizzen-Ebene bekommt ihren Satz
    const helpersFor = (rawPlane) => {
      const plane = normalizePlane(rawPlane)
      // Visuals leicht vor die Ebene heben (entlang der Normalen), damit sie
      // nicht in Teil-Oberflächen z-fighten
      const sign = plane.normal[1] === "+" ? 1 : -1
      const lift = (extra) => {
        const axis = plane.normal[0]
        const d = sign * extra
        return [ axis === "x" ? d : 0, axis === "y" ? d : 0, axis === "z" ? d : 0 ]
      }
      const toWorld = ([ u, v ], extra = 2) => {
        const [ x, y, z ] = planeToWorld(plane, u, v)
        const [ dx, dy, dz ] = lift(extra)
        return new THREE.Vector3(x + dx, y + dy, z + dz)
      }
      const addLoop = (points, material, closed = true) => {
        if (points.length < 2) return
        const geometry = new THREE.BufferGeometry().setFromPoints(points.map((p) => toWorld(p)))
        const line = closed ? new THREE.LineLoop(geometry, material) : new THREE.Line(geometry, material)
        if (material.isLineDashedMaterial) line.computeLineDistances()
        line.renderOrder = 9
        this.sketchGroup.add(line)
      }
      const addDot = (u, z, material, size = 10) => {
        const dot = new THREE.Mesh(this.sphereGeometry, material)
        dot.scale.set(size, size, size)
        dot.position.copy(toWorld([ u, z ]))
        dot.raycast = () => {}
        this.sketchGroup.add(dot)
      }
      const addFace = (points, material) => {
        if (points.length < 3) return
        // ShapeGeometry in (u, v) triangulieren, dann jeden Vertex auf die
        // Ebene abbilden — funktioniert für alle sechs Ebenenlagen gleich
        const shape2d = new THREE.Shape()
        shape2d.moveTo(points[0][0], points[0][1])
        for (let i = 1; i < points.length; i++) shape2d.lineTo(points[i][0], points[i][1])
        const geometry = new THREE.ShapeGeometry(shape2d)
        const pos = geometry.attributes.position
        for (let i = 0; i < pos.count; i++) {
          const world = toWorld([ pos.getX(i), pos.getY(i) ], 1.5)
          pos.setXYZ(i, world.x, world.y, world.z)
        }
        const mesh = new THREE.Mesh(geometry, material)
        mesh.renderOrder = 8
        mesh.raycast = () => {}
        this.sketchGroup.add(mesh)
      }
      return { addLoop, addDot, addFace }
    }
    const { addLoop, addDot, addFace } = helpersFor(data.plane)
    const shapePoints = (shape) => {
      if (shape.kind === "rect") {
        return [ [ shape.ax, shape.az ], [ shape.bx, shape.az ], [ shape.bx, shape.bz ], [ shape.ax, shape.bz ] ]
      }
      if (shape.kind === "circle") {
        return Array.from({ length: 33 }, (_, i) => {
          const a = (i / 32) * Math.PI * 2
          return [ shape.cx + Math.cos(a) * shape.r, shape.cz + Math.sin(a) * shape.r ]
        })
      }
      if (shape.kind === "line") return [ [ shape.ax, shape.az ], [ shape.bx, shape.bz ] ]
      return tessellatedPoints(shape) // poly: Eckradien als Bögen ausgerollt
    }

    // Gesperrte Maße dezent markieren: kleiner grüner Schloss-Punkt (Snap-Grün)
    // auf der Kantenmitte — beim Kreis auf dem rechten Quadrantenpunkt
    const addLockDots = (shape) => {
      for (const lockIndex of shape.locked ?? []) {
        if (shape.kind === "circle") { addDot(shape.cx + shape.r, shape.cz, this.sketchSnapMaterial, 8); continue }
        if (shape.kind === "line") { addDot((shape.ax + shape.bx) / 2, (shape.az + shape.bz) / 2, this.sketchSnapMaterial, 8); continue }
        const pts = shape.kind === "poly" ? shape.points : shapePoints(shape)
        const a = pts[lockIndex]
        const b = pts[(lockIndex + 1) % pts.length]
        if (a && b) addDot((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, this.sketchSnapMaterial, 8)
      }
    }

    const lineMaterial = data.idle ? this.sketchIdleMaterial : this.sketchMaterial
    const guideMaterial = data.idle ? this.sketchGuideIdleMaterial : this.sketchGuideMaterial
    const selectedFaces = data.selectedFaces ?? []
    data.shapes.forEach((shape, index) => {
      if (shape.kind === "line") {
        // Konstruktionslinie: gestrichelt, keine Fläche
        addLoop(shapePoints(shape), guideMaterial, false)
        if (!data.idle) addLockDots(shape)
        return
      }
      addLoop(shapePoints(shape), lineMaterial)
      // Flächen nur im aktiven Skizzenmodus füllen (antippbar fürs Extrudieren)
      if (!data.idle) {
        addFace(shapePoints(shape), selectedFaces.includes(index) ? this.sketchFaceSelectedMaterial : this.sketchFaceMaterial)
        addLockDots(shape)
      }
    })

    // Fang-Feedback: grüner Marker am gefangenen Punkt, gestrichelte
    // Bezugslinie zum Referenzpunkt beim Achsen-Fang
    const snap = data.snap
    if (snap?.point) {
      addDot(snap.point.x, snap.point.z, this.sketchSnapMaterial, 12)
      for (const ref of [ snap.refX, snap.refZ, snap.ref ]) {
        if (!ref) continue
        addDot(ref.x, ref.z, this.sketchSnapMaterial, 8)
        addLoop([ [ ref.x, ref.z ], [ snap.point.x, snap.point.z ] ], this.sketchSnapLineMaterial, false)
      }
    }

    // Gewählte Kante hervorheben (Kreis: ganze Kreislinie), mit Endpunkt-Markern
    const edge = data.selectedEdge
    if (edge && data.shapes[edge.shapeIndex]) {
      const shape = data.shapes[edge.shapeIndex]
      // poly: Kanten laufen zwischen den logischen Stützpunkten,
      // nicht entlang der tessellierten Bogenpunkte
      const points = shape.kind === "poly" ? shape.points : shapePoints(shape)
      const markers = []
      if (shape.kind === "circle") {
        addLoop(points, this.sketchEdgeMaterial)
      } else {
        const a = points[edge.edgeIndex]
        const b = points[(edge.edgeIndex + 1) % points.length]
        addLoop([ a, b ], this.sketchEdgeMaterial, false)
        markers.push(a, b)
      }
      for (const [ x, z ] of markers) addDot(x, z, this.handleActiveMaterial, 14)
    }

    // Gewählte Ecke (Radius-Bearbeitung) als Marker am logischen Stützpunkt
    const corner = data.selectedCorner
    if (corner && data.shapes[corner.shapeIndex]?.kind === "poly") {
      const point = data.shapes[corner.shapeIndex].points[corner.cornerIndex]
      if (point) addDot(point[0], point[1], this.handleActiveMaterial, 14)
    }

    if (data.draft) {
      addLoop(shapePoints(data.draft), this.sketchDraftMaterial, data.draft.kind !== "line")
    }
    if (data.poly?.length) {
      addLoop(data.poly, this.sketchDraftMaterial, false)
      // Punkt-Marker für den Linienzug
      for (const [ x, z ] of data.poly) addDot(x, z, this.handleBaseMaterial, 10)
    }

    // Weitere Skizzen dezent mitzeichnen — jede auf ihrer eigenen Ebene
    for (const layer of data.idleLayers ?? []) {
      const h = helpersFor(layer.plane)
      for (const shape of layer.shapes) {
        if (shape.kind === "line") h.addLoop(shapePoints(shape), this.sketchGuideIdleMaterial, false)
        else h.addLoop(shapePoints(shape), this.sketchIdleMaterial)
      }
    }
    this.scene.add(this.sketchGroup)
  }

  // --- Messwerkzeug -----------------------------------------------------------

  // Nur sichtbare Teile sind pick-/raycastbar — der Raycaster selbst
  // ignoriert `visible`, deshalb explizit filtern (ausgeblendete Teile,
  // Montage-Schritte)
  #pickableMeshes() {
    return [ ...this.meshes.values() ].filter((mesh) => mesh.visible)
  }

  // Punkt auf einer Teil-Oberfläche unter dem Zeiger, sonst auf dem Boden
  surfacePointAt(event) {
    this.raycaster.setFromCamera(this.#pointerNdc(event), this.camera)
    const hit = this.raycaster.intersectObjects(this.#pickableMeshes())[0]
    if (hit) return { x: hit.point.x, y: hit.point.y, z: hit.point.z }
    const floor = this.floorPointAt(event, 0)
    return floor ? { x: floor.x, y: 0, z: floor.z } : null
  }

  // Oberflächen-Treffer mit Weltnormale (für die Wahl der Skizzier-Ebene);
  // bei einem Teil-Treffer kommt dessen clientId mit (Ebenen-Kopplung),
  // ohne Teil-Treffer zählt der Boden als waagerechte Fläche
  surfaceHitAt(event) {
    this.raycaster.setFromCamera(this.#pointerNdc(event), this.camera)
    const hit = this.raycaster.intersectObjects(this.#pickableMeshes())[0]
    if (hit?.face) {
      const normal = hit.face.normal.clone().transformDirection(hit.object.matrixWorld)
      return {
        point: { x: hit.point.x, y: hit.point.y, z: hit.point.z },
        normal: { x: normal.x, y: normal.y, z: normal.z },
        clientId: hit.object.userData.clientId
      }
    }
    const floor = this.floorPointAt(event, 0)
    return floor ? { point: { x: floor.x, y: 0, z: floor.z }, normal: { x: 0, y: 1, z: 0 } } : null
  }

  // Ray-Schnitt mit einer achsparallelen Skizzier-Ebene → Ebenen-Koordinaten
  // (nicht verwechseln mit planePointAt(event, anchor, axis) der Dreh-Ringe)
  sketchPlanePointAt(event, plane) {
    const axis = plane.normal[0]
    const normalVec = new THREE.Vector3(axis === "x" ? 1 : 0, axis === "y" ? 1 : 0, axis === "z" ? 1 : 0)
    const mathPlane = new THREE.Plane(normalVec, -plane.offset)
    this.raycaster.setFromCamera(this.#pointerNdc(event), this.camera)
    const world = new THREE.Vector3()
    if (!this.raycaster.ray.intersectPlane(mathPlane, world)) return null
    return worldToPlane(plane, [ world.x, world.y, world.z ])
  }

  // data = { elements: [Punkt-/Kanten-Elemente], line: {a,b}|null, text } | null
  setMeasurement(data) {
    if (this.measureGroup) {
      this.scene.remove(this.measureGroup)
      this.measureGroup.traverse((o) => o.geometry?.dispose())
      this.measureGroup = null
    }
    this.measureData = data
    if (!this.measureLabel && this.overlay) {
      this.measureLabel = document.createElement("div")
      this.measureLabel.className = "absolute hidden -translate-x-1/2 -translate-y-full rounded-md bg-stone-900/90 " +
        "text-emerald-300 text-xs font-mono px-2 py-1 pointer-events-none whitespace-nowrap"
      this.overlay.container.appendChild(this.measureLabel)
    }
    if (!data) {
      this.measureLabel?.classList.add("hidden")
      return
    }

    this.measureMaterial ??= new THREE.LineBasicMaterial({ color: 0x34d399, depthTest: false })
    this.measureEdgeMaterial ??= new THREE.LineBasicMaterial({ color: 0x34d399, depthTest: false, transparent: true, opacity: 0.9 })
    this.measureDotMaterial ??= new THREE.MeshBasicMaterial({ color: 0x34d399, depthTest: false })
    this.measureFreeDotMaterial ??= new THREE.MeshBasicMaterial({ color: 0xa8a29e, depthTest: false })
    this.measureGroup = new THREE.Group()
    const addDot = (p, material, size) => {
      const dot = new THREE.Mesh(this.sphereGeometry, material)
      dot.scale.set(size, size, size)
      dot.position.set(p.x, p.y, p.z)
      dot.renderOrder = 11
      dot.raycast = () => {}
      this.measureGroup.add(dot)
    }
    const addLine = (a, b, material) => {
      const geometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(a.x, a.y, a.z), new THREE.Vector3(b.x, b.y, b.z)
      ])
      const line = new THREE.Line(geometry, material)
      line.renderOrder = 11
      this.measureGroup.add(line)
    }

    for (const element of data.elements ?? []) {
      if (element.type === "edge") {
        // gewählte Kante komplett hervorheben
        addLine(element.a, element.b, this.measureEdgeMaterial)
        addDot(element.a, this.measureDotMaterial, 8)
        addDot(element.b, this.measureDotMaterial, 8)
      } else {
        // eingerastete Bezugspunkte kräftig grün, freie Punkte dezent grau
        const snapped = element.kind !== "free"
        addDot(element.point, snapped ? this.measureDotMaterial : this.measureFreeDotMaterial, snapped ? 14 : 10)
      }
    }
    if (data.line) addLine(data.line.a, data.line.b, this.measureMaterial)
    this.scene.add(this.measureGroup)
  }

  #tickMeasureLabel() {
    if (!this.measureLabel) return
    const m = this.measureData
    if (!m?.line || !m.text) { this.measureLabel.classList.add("hidden"); return }
    const { a, b } = m.line
    const mid = new THREE.Vector3((a.x + b.x) / 2, (a.y + b.y) / 2 + 20, (a.z + b.z) / 2).project(this.camera)
    if (mid.z > 1) { this.measureLabel.classList.add("hidden"); return }
    const rect = this.canvas.getBoundingClientRect()
    this.measureLabel.classList.remove("hidden")
    this.measureLabel.textContent = m.text
    this.measureLabel.style.left = `${(mid.x * 0.5 + 0.5) * rect.width}px`
    this.measureLabel.style.top = `${(-mid.y * 0.5 + 0.5) * rect.height}px`
  }

  // Schwerpunkt: kleine Kugel + Lot auf den Boden mit Fadenkreuz am Aufsetzpunkt
  // (gebaut wie der Mess-Marker). data = { x, y, z, stable } | null.
  // Steht das Lot außerhalb der Standfläche, wird der Marker rot statt grün.
  setCenterOfMass(data) {
    if (this.comGroup) {
      this.scene.remove(this.comGroup)
      this.comGroup.traverse((o) => o.geometry?.dispose())
      this.comGroup = null
    }
    if (!data) return

    this.comStableMaterial ??= new THREE.MeshBasicMaterial({ color: 0x0ea5e9, depthTest: false })
    this.comRiskMaterial ??= new THREE.MeshBasicMaterial({ color: 0xef4444, depthTest: false })
    this.comStableLineMaterial ??= new THREE.LineBasicMaterial({ color: 0x0ea5e9, depthTest: false })
    this.comRiskLineMaterial ??= new THREE.LineBasicMaterial({ color: 0xef4444, depthTest: false })
    const dotMaterial = data.stable === false ? this.comRiskMaterial : this.comStableMaterial
    const lineMaterial = data.stable === false ? this.comRiskLineMaterial : this.comStableLineMaterial

    this.comGroup = new THREE.Group()
    const addLine = (a, b) => {
      const geometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(a.x, a.y, a.z), new THREE.Vector3(b.x, b.y, b.z)
      ])
      const line = new THREE.Line(geometry, lineMaterial)
      line.renderOrder = 11
      line.raycast = () => {}
      this.comGroup.add(line)
    }

    const dot = new THREE.Mesh(this.sphereGeometry, dotMaterial)
    dot.scale.set(22, 22, 22)
    dot.position.set(data.x, data.y, data.z)
    dot.renderOrder = 11
    dot.raycast = () => {}
    this.comGroup.add(dot)

    // Lot auf den Boden + Fadenkreuz, damit die Standfläche beurteilbar bleibt
    addLine({ x: data.x, y: data.y, z: data.z }, { x: data.x, y: 0, z: data.z })
    const arm = 60
    addLine({ x: data.x - arm, y: 0, z: data.z }, { x: data.x + arm, y: 0, z: data.z })
    addLine({ x: data.x, y: 0, z: data.z - arm }, { x: data.x, y: 0, z: data.z + arm })

    this.scene.add(this.comGroup)
  }

  focusOnPart(clientId) {
    const mesh = this.meshes.get(clientId)
    if (!mesh) return
    const dims = mesh.userData.dims ?? { x: 400, y: 400, z: 400 }
    this.#animateFocus(mesh.position.clone(), Math.max(dims.x, dims.y, dims.z) * 1.8)
  }

  frameAll() {
    const dims = this.boundsAnchor.userData.dims
    if (!dims) return
    this.#animateFocus(this.boundsAnchor.position.clone(), Math.max(dims.x, dims.y, dims.z, 500) * 1.5)
  }

  #animateFocus(center, distance) {
    const clamped = Math.min(Math.max(distance, this.controls.minDistance), this.controls.maxDistance)
    const direction = this.camera.position.clone().sub(this.controls.target).normalize()
    this.focusAnim = {
      fromTarget: this.controls.target.clone(),
      toTarget: center,
      fromPosition: this.camera.position.clone(),
      toPosition: center.clone().add(direction.multiplyScalar(clamped)),
      start: performance.now()
    }
  }

  #tickFocus() {
    if (!this.focusAnim) return
    const raw = Math.min(1, (performance.now() - this.focusAnim.start) / 250)
    const eased = 1 - Math.pow(1 - raw, 3)
    this.controls.target.lerpVectors(this.focusAnim.fromTarget, this.focusAnim.toTarget, eased)
    this.camera.position.lerpVectors(this.focusAnim.fromPosition, this.focusAnim.toPosition, eased)
    if (raw >= 1) this.focusAnim = null
  }

  // Gestrichelte Linien zwischen verbundenen Teilen (Paare von client_ids).
  // Endpunkte werden pro Frame aus den Mesh-Positionen aktualisiert,
  // damit sie auch der Drag-Vorschau folgen.
  setRelations(pairs) {
    this.relationLines.forEach((line) => {
      this.scene.remove(line)
      line.geometry.dispose()
    })
    this.relationMaterial ??= new THREE.LineDashedMaterial({
      color: 0x38bdf8, dashSize: 40, gapSize: 25, transparent: true, opacity: 0.9
    })
    this.relationLines = pairs.map(([fromId, toId]) => {
      const geometry = new THREE.BufferGeometry().setFromPoints([ new THREE.Vector3(), new THREE.Vector3() ])
      const line = new THREE.Line(geometry, this.relationMaterial)
      line.userData.pair = [fromId, toId]
      line.raycast = () => {}
      this.scene.add(line)
      return line
    })
  }

  #updateRelationLines() {
    for (const line of this.relationLines) {
      // im Foto-Modus bleiben Beziehungs-Linien aus — der Tick würde sie
      // sonst pro Frame wieder einblenden
      if (this.photoMode) { line.visible = false; continue }
      const [fromId, toId] = line.userData.pair
      const from = this.meshes.get(fromId)
      const to = this.meshes.get(toId)
      if (!from || !to) { line.visible = false; continue }
      line.visible = true
      const positions = line.geometry.attributes.position
      positions.setXYZ(0, from.position.x, from.position.y, from.position.z)
      positions.setXYZ(1, to.position.x, to.position.y, to.position.z)
      positions.needsUpdate = true
      line.computeLineDistances()
    }
  }

  // --- Picking --------------------------------------------------------------

  #pointerNdc(event) {
    const rect = this.canvas.getBoundingClientRect()
    return new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    )
  }

  pickPart(event) {
    this.raycaster.setFromCamera(this.#pointerNdc(event), this.camera)
    const hit = this.raycaster.intersectObjects(this.#pickableMeshes())[0]
    return hit?.object.userData.clientId ?? null
  }

  // Senkrechtes Ziehen: Schnitt des Zeiger-Strahls mit der vertikalen,
  // zur Kamera gewandten Ebene durch den Ankerpunkt → liefert die Welt-Y-Höhe
  verticalPointAt(event, anchor) {
    this.raycaster.setFromCamera(this.#pointerNdc(event), this.camera)
    const normal = this.camera.getWorldDirection(new THREE.Vector3())
    normal.y = 0
    if (normal.lengthSq() < 0.0001) normal.set(0, 0, 1)
    normal.normalize()
    const anchorVector = new THREE.Vector3(anchor.x, anchor.y, anchor.z)
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, anchorVector)
    const point = new THREE.Vector3()
    return this.raycaster.ray.intersectPlane(plane, point) ? point.y : null
  }

  // Zeigerpunkt in der Ring-Ebene (Ebene durch anchor mit Achsen-Normale)
  planePointAt(event, anchor, axisKey) {
    this.raycaster.setFromCamera(this.#pointerNdc(event), this.camera)
    const normals = { x: [ 1, 0, 0 ], y: [ 0, 1, 0 ], z: [ 0, 0, 1 ] }
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      new THREE.Vector3(...normals[axisKey]),
      new THREE.Vector3(anchor.x, anchor.y, anchor.z)
    )
    const point = new THREE.Vector3()
    return this.raycaster.ray.intersectPlane(plane, point)
      ? { x: point.x, y: point.y, z: point.z }
      : null
  }

  // Transiente Rotations-Vorschau beim Ring-Drag
  setPreviewRotation(clientId, rot) {
    const mesh = this.meshes.get(clientId)
    if (!mesh) return
    mesh.rotation.set(rot.rot_x * DEG, rot.rot_y * DEG, rot.rot_z * DEG)
    const strips = this.edgeStrips.get(clientId)
    if (strips) this.#positionAttachedMeshes(strips, mesh)
    const bores = this.boreMeshes.get(clientId)
    if (bores) this.#positionAttachedMeshes(bores, mesh)
    if (clientId === this.selectedId) this.#syncHandlePositions()
  }

  // Schnittpunkt mit horizontaler Ebene auf Höhe y (für Drag auf Arbeitsebene)
  floorPointAt(event, y = 0) {
    this.raycaster.setFromCamera(this.#pointerNdc(event), this.camera)
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -y)
    const point = new THREE.Vector3()
    return this.raycaster.ray.intersectPlane(plane, point) ? point : null
  }

  cameraState() {
    return { position: this.camera.position.toArray(), target: this.controls.target.toArray() }
  }

  restoreCamera(state) {
    if (!state?.position || !state?.target) return
    this.camera.position.fromArray(state.position)
    this.controls.target.fromArray(state.target)
    this.controls.update()
  }

  // --- Intern ---------------------------------------------------------------

  #buildEnvironment() {
    // Referenzen behalten: der Foto-Modus blendet Grid/Achsen aus und
    // schaltet die Werkstatt-Lichter gegen ein weiches Setup
    this.hemisphere = new THREE.HemisphereLight(0xffffff, 0x57534e, 1.1)
    this.scene.add(this.hemisphere)

    this.sun = new THREE.DirectionalLight(0xffffff, 1.4)
    this.sun.position.set(2000, 3000, 1500)
    this.scene.add(this.sun)

    this.grid = new THREE.GridHelper(GRID_SIZE, GRID_SIZE / GRID_CELL, 0x78716c, 0x44403c)
    this.scene.add(this.grid)

    this.axes = new THREE.AxesHelper(300)
    this.axes.position.y = 1
    this.scene.add(this.axes)
  }

  #resize() {
    const { clientWidth, clientHeight } = this.canvas.parentElement
    if (clientWidth === 0 || clientHeight === 0) return
    this.renderer.setSize(clientWidth, clientHeight, false)
    this.camera.aspect = clientWidth / clientHeight
    this.camera.updateProjectionMatrix()
  }

  #tick() {
    this.#tickFocus()
    this.controls.update()
    this.#updateRelationLines()
    this.#tickMeasureLabel()
    if (this.overlay && this.selectedId) {
      const mesh = this.meshes.get(this.selectedId)
      if (mesh) this.overlay.update(mesh, this.camera, this.canvas)
    }
    this.#tickSelectedScreenPos()
    if (this.boundsOverlay) {
      if (!this.selectedId && this.boundsAnchor.userData.dims) {
        this.boundsOverlay.update(this.boundsAnchor, this.camera, this.canvas)
      } else {
        this.boundsOverlay.hide()
      }
    }
    this.renderer.render(this.scene, this.camera)
  }

  // Bildschirmposition der Oberkante des selektierten Teils — Anker für die
  // schwebende Kontextleiste (Aktionen erscheinen am Objekt, Shapr3D-Stil)
  #tickSelectedScreenPos() {
    if (!this.onSelectedScreenPos) return
    const mesh = this.selectedId ? this.meshes.get(this.selectedId) : null
    if (!mesh) return
    const box = new THREE.Box3().setFromObject(mesh)
    const top = new THREE.Vector3()
    box.getCenter(top)
    top.y = box.max.y
    top.project(this.camera)
    this.onSelectedScreenPos({
      x: ((top.x + 1) / 2) * this.canvas.clientWidth,
      y: ((1 - top.y) / 2) * this.canvas.clientHeight
    })
  }
}
